import { DataGridPro, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from "react";
import { Box, IconButton, LinearProgress, Select, MenuItem, FormControl } from '@mui/material';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import useAuth from "../../context/AuthProvider";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Swal from 'sweetalert2';
import { RiExpandUpDownLine } from "react-icons/ri";
import { StateLoginsModel } from '../../models/StateLoginsModel';
import { AddEditStateLogins } from '../AddEdit/AddEditStateLogins';
import { isUserRoleInclude, readXlsxFile, safeTrim, writeXlsxFile } from '../../helper/utils';
import { apiClient } from '../../helper/api';
import moment from 'moment';

interface Iprops {
    data: StateLoginsModel[];
    fetchData: () => void;
    selectedPartner: string;
    setSelectedPartner: React.Dispatch<React.SetStateAction<string>>;
    selectedLicensee: string;
    setSelectedLicensee: React.Dispatch<React.SetStateAction<string>>;
    selectedState: string;
    setSelectedState: React.Dispatch<React.SetStateAction<string>>;
}

interface StateList {
    stateId: string;
    stateName: string;
    postalAbbv: string;
}

export const StateLoginsList: React.FC<Iprops> = ({ 
    data, 
    fetchData, 
    selectedPartner, 
    setSelectedPartner, 
    selectedLicensee, 
    setSelectedLicensee, 
    selectedState, 
    setSelectedState 
}) => {
    const initialStateData = { stateId: "", stateName: "", postalAbbv: "" };
    const initialData: StateLoginsModel = {
        stateLoginsId: 0,
        stateId: 0,
        statename: '',
        standardAbbv: '',
        postalAbbv: '',
        partnersId: 0,
        licensee: '',
        website: '',
        username: '',
        password1: '',
        password2: '',
        client: '',
        accountID: '',
    };

    const [states, setStates] = useState<StateList[]>([initialStateData]);
    const [open, setOpen] = React.useState(false);
    const [formData, setFormData] = React.useState(initialData);
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = React.useState(false);
    const initialPartnerData = { partnersId: 0, customerName: "" };
    const [partners, setPartnerData] = React.useState([initialPartnerData]);
    const [licenseeNames, setLicenseeNames] = useState<{ rowNum: number; licenseeName: string }[]>([]);
    const apiRef = useGridApiRef();

    const additionalStates = [
        { stateId: 'CRES', stateName: 'CRES', postalAbbv: 'CRES' },
        { stateId: 'InsCipher', stateName: 'InsCipher', postalAbbv: 'InsCipher' },
        { stateId: 'OPTins', stateName: 'OPTins', postalAbbv: 'OPTins' },
        { stateId: 'Sircon', stateName: 'Sircon', postalAbbv: 'Sircon' },
        { stateId: 'SLAS', stateName: 'SLAS', postalAbbv: 'SLAS' }
    ];

    const additionalClients = [
        { partnersId: 'ApplePie Capital Insurance Solutions', customerName: 'ApplePie Capital Insurance Solutions' },
        { partnersId: 'Cyberwrite', customerName: 'Cyberwrite' },
        { partnersId: 'Insurance Services of San Francisco, Inc. (ISU)', customerName: 'Insurance Services of San Francisco, Inc. (ISU)' },
        { partnersId: "MISC", customerName: "MISC" },
        { partnersId: 'VoloFin', customerName: 'VoloFin' },
        { partnersId: 'Worldwide', customerName: 'Worldwide' }
    ];

    const fetchRequiredData = async (action: string): Promise<any[]> => {
        try {
            const response = await apiClient(action, {
                method: "GET",
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
            });

            return response?.data || [];
        } catch (error) {
            console.error(`Error fetching ${action}:`, error);
            return [];
        }
    };

    const setRequiredStates = async () => {
        try {
            const [partners, licenseeNames, states] = await Promise.all([
                fetchRequiredData('/Partners/getPartners'),
                fetchRequiredData('/States/getStatesLoginsLicenseeNames'),
                fetchRequiredData('/States/getStates'),
            ]);

            setPartnerData([...partners, ...additionalClients]);
            setLicenseeNames(licenseeNames);
            setStates([...states, ...additionalStates]);
        } catch (error) {
            console.error('Error setting required states:', error);
        }
    };

    useEffect(() => {
        setRequiredStates();
    }, []);

    const getPartnerFetch = () => {
        setFormData(initialData);
        setOpen(true);
    };

    const handleCopy = (value: string | undefined) => {
        if (!value) return;
        navigator.clipboard.writeText(value);
    };

    const handleEdit = (item: StateLoginsModel) => {
        setFormData(item);
        setOpen(true);
    };

    const PasswordShow = ({ value }: { value: string }) => {
        const [showPassword, setShowPassword] = useState<boolean>(false);

        const handleShowPassword = () => {
            setShowPassword(!showPassword);
        };

        return (
            <div className='password-cell'>
                {value ? (
                    <>
                        <input
                            className=''
                            type={'text'}
                            value={showPassword ? value : '*************'}
                            disabled
                        />
                        <button
                            className='btn'
                            onClick={() => handleShowPassword()}
                        >
                            {showPassword ? 'Hide' : 'View'}
                        </button>
                    </>
                ) : (
                    ''
                )}
            </div>
        );
    };

    const columns: GridColDef[] = [
        {
            field: 'licensee',
            type: 'string',
            headerName: 'Licensee',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'postalAbbv',
            type: 'string',
            headerName: 'State',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'website',
            type: 'string',
            headerName: 'Website',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'username',
            type: 'string',
            headerName: 'Username',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'copyUsername',
            headerAlign: 'center',
            align: 'center',
            headerName: "",
            hideSortIcons: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            width: 40,
            renderCell: (params) => {
                if (!params.row.username) return <></>;
                return (
                    <div>
                        <IconButton
                            color="primary"
                            onClick={() => { handleCopy(params.row.username); }}
                        >
                            <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.11765 15.7059C3.9481 15.7059 3 14.7578 3 13.5882V5.11765C3 3.9481 3.9481 3 5.11765 3H13.5882C14.7578 3 15.7059 3.9481 15.7059 5.11765V5.64706" stroke="#162E3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <rect x="8.29413" y="8.29412" width="12.7059" height="12.7059" rx="2" stroke="#162E3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </IconButton>
                    </div>
                );
            }
        },
        {
            field: 'password1',
            type: 'string',
            headerName: 'Password1',
            hideSortIcons: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                return <PasswordShow value={params.row.password1} />;
            }
        },
        {
            field: 'copyPassword1',
            headerAlign: 'center',
            align: 'center',
            headerName: "",
            hideSortIcons: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            width: 40,
            renderCell: (params) => {
                if (!params.row.password1) return <></>;
                return (
                    <div>
                        <IconButton
                            color="primary"
                            onClick={() => { handleCopy(params.row.password1); }}
                        >
                            <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.11765 15.7059C3.9481 15.7059 3 14.7578 3 13.5882V5.11765C3 3.9481 3.9481 3 5.11765 3H13.5882C14.7578 3 15.7059 3.9481 15.7059 5.11765V5.64706" stroke="#162E3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <rect x="8.29413" y="8.29412" width="12.7059" height="12.7059" rx="2" stroke="#162E3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </IconButton>
                    </div>
                );
            }
        },
        {
            field: 'password2',
            type: 'string',
            headerName: 'Password2',
            hideSortIcons: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                return <PasswordShow value={params.row.password2} />;
            }
        },
        {
            field: 'copyPassword2',
            headerAlign: 'center',
            align: 'center',
            headerName: "",
            hideSortIcons: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            width: 40,
            renderCell: (params) => {
                if (!params.row.password2) return <></>;
                return (
                    <div>
                        <IconButton
                            color="primary"
                            onClick={() => { handleCopy(params.row.password2); }}
                        >
                            <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.11765 15.7059C3.9481 15.7059 3 14.7578 3 13.5882V5.11765C3 3.9481 3.9481 3 5.11765 3H13.5882C14.7578 3 15.7059 3.9481 15.7059 5.11765V5.64706" stroke="#162E3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <rect x="8.29413" y="8.29412" width="12.7059" height="12.7059" rx="2" stroke="#162E3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </IconButton>
                    </div>
                );
            }
        },
        {
            field: 'notes',
            type: 'string',
            headerName: 'Notes',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'client',
            type: 'string',
            headerName: 'Client',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            flex: 0.8,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'stateLoginsId',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            headerName: "Action",
            hideSortIcons: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <div>
                    <IconButton
                        color="primary"
                        onClick={() => { handleEdit(params.row); }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 3C17.2626 2.73736 17.5744 2.52902 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52902 20.7374 2.73736 21 3C21.2626 3.26264 21.471 3.57445 21.6131 3.91761C21.7553 4.26077 21.8284 4.62857 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73736 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" stroke="#02A4E3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </IconButton>

                    <IconButton
                        color="error"
                        onClick={() => { handleDelete(params.row.stateLoginsId); }}
                    >
                        {params.row.isDeleted ? (
                            <RestoreFromTrashIcon />
                        ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="#162E3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </IconButton>
                </div>
            )
        },
    ];

    const handleClickAdd = () => {
        getPartnerFetch();
    };

    const handleClose = () => {
        setFormData(initialData);
        fetchData();
        setOpen(false);
    };

    const xlsHeader = [
        'ID',
        'Licensee',
        'State',
        'Website',
        'Username',
        'Password1',
        'Password2',
        'Notes',
        'Client',
        'AccountID'
    ];

    const columnList = [
        {
            id: 0,
            field: 'id',
            xlsHeader: 'ID',
            inputType: 'number',
        },
        {
            id: 0,
            field: 'licensee',
            xlsHeader: 'Licensee',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'postalAbbv',
            xlsHeader: 'State',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'website',
            xlsHeader: 'Website',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'username',
            xlsHeader: 'Username',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'password1',
            xlsHeader: 'Password1',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'password2',
            xlsHeader: 'Password2',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'notes',
            xlsHeader: 'Notes',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'client',
            xlsHeader: 'Client',
            inputType: 'string',
        },
        {
            id: 0,
            field: 'accountID',
            xlsHeader: 'AccountID',
            inputType: 'string',
        },
    ];

    const handleExportXlsx = () => {
        const visibleRows = Array.from(apiRef.current.getVisibleRowModels().values());
        const xlsData: any[][] = [xlsHeader];

        console.log("Data Length:" + visibleRows.length);
        visibleRows.map((item, index) => ({
            id: index + 1,
            ...item,
        })).forEach(item => {
            const update: any[] = [];
            xlsHeader.map(header => {
                const column = columnList.find(c => c.xlsHeader === header);
                if (!column) {
                    update.push('');
                    return;
                }
                const field = column.field as keyof StateLoginsModel;
                const value = item[field];
                if (value === undefined || value === null) {
                    update.push('');
                    return;
                }
                switch (column.inputType) {
                    case 'string':
                        update.push(value.toString());
                        break;
                    case 'number':
                        update.push(+value);
                        break;
                    default:
                        update.push(value.toString());
                        break;
                }
            });
            xlsData.push(update);
        });

        writeXlsxFile(xlsData, 'MISC_StateLogins.xlsx');
    };

    const handleFileChange = async (event: any) => {
        const file = event.target.files[0];
        if (file) {
            setLoading(true);
            await importXLS(file);
            setLoading(false);
        }
    };

    const importXLS = async (file: File) => {
        const sheetsData = await readXlsxFile(file);
        const sheetName = Object.keys(sheetsData)[0];
        const importedData = sheetsData[sheetName];
        const header = importedData[0];
        const stateLookup = new Map(states.map(state => [state.postalAbbv, state.stateId]));
        const updateItems: any[] = [];

        importedData.slice(1).forEach((item: Array<string>) => {
            const stateName = safeTrim(item[header.indexOf('State')]);
            const licensee = safeTrim(item[header.indexOf('Licensee')]);
            const website = safeTrim(item[header.indexOf('Website')]);
            const username = safeTrim(item[header.indexOf('Username')]);
            const password1 = safeTrim(item[header.indexOf('Password1')]);
            const password2 = safeTrim(item[header.indexOf('Password2')]);
            const notes = safeTrim(item[header.indexOf('Notes')]);
            const client = safeTrim(item[header.indexOf('Client')]);
            const accountID = safeTrim(item[header.indexOf('AccountID')]);
            const stateId = stateLookup.get(stateName);
            const customer = partners.find((part) => {
                if (part.customerName?.substring(0, 14) === client?.substring(0, 14)) {
                    return true;
                }
                return false;
            });

            updateItems.push({
                licensee: licensee,
                website: website,
                username: username,
                password1: password1,
                password2: password2,
                notes: notes,
                client: customer?.customerName || client,
                stateId: stateId,
                partnersId: customer ? customer?.partnersId : null,
                stateName: stateName,
                accountID: accountID?.toString() || ''
            });
        });

        const existingRecords = new Set(data.map(eRow =>
            `${safeTrim(eRow.licensee, '')}|${safeTrim(eRow.postalAbbv, '')}|${safeTrim(eRow.website, '')}|${safeTrim(eRow.username, '')}|${safeTrim(eRow.notes, '')}|${safeTrim(eRow.client?.substring(0, 14), '')}|${safeTrim(eRow.password1, '')}|${safeTrim(eRow.password2, '')}|${safeTrim(eRow.accountID, '')}`
        ));

        const newItems: any[] = [];
        const duplicatedItems: any[] = [];
        updateItems.forEach((nRow: any) => {
            const key = `${safeTrim(nRow.licensee, '')}|${safeTrim(nRow.stateName, '')}|${safeTrim(nRow.website, '')}|${safeTrim(nRow.username, '')}|${safeTrim(nRow.notes, '')}|${safeTrim(nRow.client?.substring(0, 14), '')}|${safeTrim(nRow.password1, '')}|${safeTrim(nRow.password2, '')}|${safeTrim(nRow.accountID, '')}`;
            if (!existingRecords.has(key)) {
                newItems.push(nRow);
                return;
            }
            duplicatedItems.push(nRow);
        });

        const sanitizedNewItems = newItems.map(item => Object.fromEntries(
            Object.entries(item).map(([key, value]) => [key, String(value ?? '')]))
        );
        const timeStamp = moment().format('YYYY.M.D');

        await apiClient('/StateLogins/MultiSaveOrUpdate', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify(sanitizedNewItems)
        }).then(async (res) => {
            if (!res) return;
            if (res.data && res.data.length > 0) {
                let header = Object.keys(res.data[0]);
                let rows = res.data.map((obj: any) => Object.values(obj));
                writeXlsxFile([header, ...rows], 'Invalid_StateLogins.xlsx');
            }
            if (res.status === true) {
                Swal.fire({
                    title: res.message || "Data Successfully saved",
                    icon: "success",
                    confirmButtonText: "OK",
                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: res.message || "Something went wrong while saving data.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        })
        .catch((error) => {
            console.error("API error:", error);
            Swal.fire({
                title: "Server Error",
                text: error.message || "Failed to connect to the server.",
                icon: "error",
                confirmButtonText: "OK",
            });
        })
        .finally(() => {
            fetchData();
        });
    };

    const handleDelete = (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            iconHtml: '<svg width="70" height="70" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="31" cy="31" r="31" fill="#02A4E3"/><path d="M31.0003 25.6667V31M31.0003 36.3334H31.0137M44.3337 31C44.3337 38.3638 38.3641 44.3334 31.0003 44.3334C23.6365 44.3334 17.667 38.3638 17.667 31C17.667 23.6362 23.6365 17.6667 31.0003 17.6667C38.3641 17.6667 44.3337 23.6362 44.3337 31Z" stroke="white" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
            showCancelButton: true,
            confirmButtonColor: '#02A4E3',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Delete',
            customClass: {
                confirmButton: 'btn-radius',
                cancelButton: 'btn-radius',
                popup: 'card-radius'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                apiClient(`/StateLogins/Delete/${id}`, {
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }),
                    method: "DELETE",
                })
                .then(async (res) => {
                    if (res.status === true) {
                        fetchData();
                        setOpen(false);
                        Swal.fire(
                            'Deleted!',
                            'The record has been successfully deleted.',
                            'success'
                        );
                    } else {
                        Swal.fire(
                            'Failed!',
                            res?.message || 'Something went wrong while deleting.',
                            'error'
                        );
                    }
                })
                .catch((error) => {
                    console.error(error);
                    Swal.fire(
                        'Failed!',
                        'An unexpected error occurred.',
                        'error'
                    );
                });
            }
        });
    };

    function EditToolbar() {
        return (
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    p: 1,
                }}
            >
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="">
                        <span className="card-title">State Logins</span>
                    </div>
                    <div className="d-flex gap-4">
                        {/* Licensee Dropdown */}
                        <FormControl size="small" sx={{ minWidth: 250, marginRight: '10px' }}>
                            <Select
                                value={selectedLicensee}
                                onChange={(e) => setSelectedLicensee(e.target.value)}
                                displayEmpty
                                autoWidth
                                IconComponent={ExpandMoreIcon}
                                renderValue={(selected: any) =>
                                    selected ? selected : 'Select Licensee'
                                }
                                sx={{
                                    textAlign: 'left',
                                    '& .MuiSelect-select': {
                                        textAlign: 'left',
                                        paddingLeft: '10px',
                                    },
                                }}
                            >
                                <MenuItem value={''}>Select Licensee</MenuItem>
                                {licenseeNames
                                    ?.filter(item => item.licenseeName !== null && item.licenseeName !== "")
                                    .map(item => (
                                        <MenuItem
                                            key={item.rowNum}
                                            className="custom-menu-item"
                                            value={item.licenseeName}
                                        >
                                            {item.licenseeName}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        {/* State Dropdown */}
                        <FormControl size="small" sx={{ minWidth: 250, marginRight: '10px' }}>
                            <Select
                                value={selectedState}
                                onChange={(e) => setSelectedState(e.target.value)}
                                autoWidth
                                IconComponent={ExpandMoreIcon}
                                displayEmpty
                                sx={{
                                    textAlign: 'left',
                                    '& .MuiSelect-select': {
                                        textAlign: 'left',
                                        paddingLeft: '10px',
                                    },
                                }}
                            >
                                <MenuItem value={''}>Select State</MenuItem>
                                {states?.map((item) => (
                                    <MenuItem
                                        key={item.stateId}
                                        className="custom-menu-item"
                                        value={item.stateId}
                                    >
                                        {item.stateName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {/* Client Dropdown */}
                        <FormControl size="small" sx={{ minWidth: 250, marginRight: '10px' }}>
                            <Select
                                value={selectedPartner}
                                onChange={(e) => setSelectedPartner(e.target.value)}
                                autoWidth
                                IconComponent={ExpandMoreIcon}
                                displayEmpty
                                renderValue={(selected: any) => {
                                    const selectedItem = partners?.find(item => item.partnersId === selected);
                                    return selectedItem ? selectedItem.customerName : 'Select Client';
                                }}
                                sx={{
                                    textAlign: 'left',
                                    '& .MuiSelect-select': {
                                        textAlign: 'left',
                                        paddingLeft: '10px',
                                    },
                                }}
                            >
                                <MenuItem value={''}>Select Client</MenuItem>
                                {partners?.map((item) => (
                                    <MenuItem
                                        key={item.partnersId}
                                        className="custom-menu-item"
                                        value={item.partnersId}
                                    >
                                        {item.customerName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <button
                            className="active-btn-blue"
                            onClick={() => { window.location.reload(); }}
                        >
                            Refresh
                        </button>
                        <button
                            className="active-btn-blue"
                            onClick={() => document.getElementById("importXLS")?.click()}
                        >
                            Import
                        </button>
                        <input
                            type="file"
                            id="importXLS"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                            accept=".xls,.xlsx"
                        />
                        <button
                            className="active-btn-blue"
                            onClick={handleClickAdd}
                        >
                            Add
                        </button>
                        {isUserRoleInclude(user?.userRoles.split(","), ["Super Admin"]) && (
                            <button
                                onClick={() => {
                                    handleExportXlsx();
                                }}
                                className="active-btn-blue"
                            >
                                Export
                            </button>
                        )}
                    </div>
                </div>
            </Box>
        );
    }

    return (
        <div style={{ height: '60vh', width: '100%' }}>
            {loading && <LinearProgress style={{ position: 'fixed', top: 0, width: '100%', left: 0 }} />}
            <DataGridPro
                getRowId={(r) => r.stateLoginsId}
                rows={data}
                columns={columns}
                style={{ textAlign: 'center' }}
                components={{
                    Toolbar: EditToolbar,
                }}
                apiRef={apiRef}
                sx={{
                    fontFamily: "'Proxima Nova', sans-serif;",
                    border: "0px",
                    fontSize: "16px",
                    '& .MuiDataGrid-columnHeaderTitle': {
                        whiteSpace: 'break-spaces',
                        lineHeight: 1,
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
                        pl: 1,
                    },
                }}
            />
            {<AddEditStateLogins
                handleClose={handleClose}
                Open={open}
                item={formData}
                initValues={initialData}
                states={states}
                partners={partners}
            />}
        </div>
    );
};